"use client";

import React, { type FC, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import type { IFilter } from "@/src/shared/features/search/types";
import GuestsModal from "@/src/shared/features/search/components/GuestsModal";
import type { IListing } from "@/src/shared/types/listing";
import { useLockBodyScroll } from "@/src/shared/hooks/useLockBodyScroll";

interface IBookingGuestModalProps {
  onBack: () => void;
  filters: IFilter;
  updateGuestCount: (
    type: keyof IFilter["guests"],
    op: "add" | "subtract",
  ) => void;
  onSave?: () => void;
  listing?: IListing;
  /**
   * The app's guest sheet works on a copy: with this set the sheet hands the counts over on
   * its button, and Cancel leaves them as they were.
   */
  onDoneGuests?: (guests: IFilter["guests"]) => void;
  doneLabel?: string;
  /** "app": the app's guest sheet, opened over the screen under it (the dates) with a dimmed backdrop. */
  appearance?: "default" | "app";
}

const BookingGuestModal: FC<IBookingGuestModalProps> = ({
  listing,
  onBack,
  filters,
  updateGuestCount,
  onSave,
  onDoneGuests,
  doneLabel = "Save",
  appearance = "default",
}) => {
  useLockBodyScroll();
  const [draft, setDraft] = useState(filters.guests);
  const shownFilters = onDoneGuests ? { ...filters, guests: draft } : filters;
  const changeGuestCount = (
    type: keyof IFilter["guests"],
    op: "add" | "subtract",
  ) =>
    onDoneGuests
      ? setDraft((prev) => ({
          ...prev,
          [type]: Math.max(0, (prev[type] ?? 0) + (op === "add" ? 1 : -1)),
        }))
      : updateGuestCount(type, op);
  const showRooms =
    !listing && (filters.destination?.name || "").toLowerCase().includes("cox");
  const confirm = () => {
    if (onDoneGuests) {
      onDoneGuests(draft);
      return;
    }
    if (onSave) {
      onSave();
    }
    onBack();
  };

  if (appearance === "app") {
    return (
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onBack}
          className="fixed inset-0 z-[55] bg-black/40"
        />
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 30, stiffness: 300 }}
          role="dialog"
          aria-modal
          aria-label="Select guests"
          className="fixed inset-x-0 bottom-0 z-[60] rounded-t-[25px] bg-white px-6 pt-6 pb-[max(10px,env(safe-area-inset-bottom))]"
        >
          <div className="flex items-center">
            <h2 className="flex-1 text-16 font-semibold text-ink-black">
              {showRooms ? "Select Rooms and Guests" : "Select Guest Size"}
            </h2>
            <button
              type="button"
              onClick={onBack}
              className="text-14 font-medium text-ink-gray"
            >
              Cancel
            </button>
          </div>
          <div className="mt-6">
            <GuestsModal
              isAppliedValidation={true}
              maxAdults={listing?.max_guest}
              maxInfants={listing?.max_infant}
              maxChildren={listing?.max_child}
              filters={shownFilters}
              updateGuestCount={changeGuestCount}
              showRooms={showRooms}
              stepper="app"
            />
          </div>
          <div className="mt-4 flex gap-6">
            <button
              type="button"
              onClick={onBack}
              className="h-14 flex-1 rounded-lg border border-[#E0E0E0] bg-white text-16 text-ink-gray"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirm}
              className="h-14 flex-1 rounded-lg bg-brand text-16 text-white active:opacity-80"
            >
              {doneLabel}
            </button>
          </div>
        </motion.div>
      </>
    );
  }

  return (
    <motion.div
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      exit={{ y: "100%" }}
      transition={{ type: "spring", damping: 30, stiffness: 300 }}
      className="fixed bottom-0 left-0 right-0 z-50 bg-white p-4 border shadow-xl rounded-t-3xl"
    >
      <div className="flex items-center justify-between pb-1 border-b border-gray-200">
        <button type="button" onClick={onBack} className="p-2">
          <ArrowLeft size={24} />
        </button>
        <h2 className="text-lg font-semibold">Select Guest</h2>
        <div></div>
      </div>

      <div className="mt-10">
        <GuestsModal
          isAppliedValidation={true}
          maxAdults={listing?.max_guest}
          maxInfants={listing?.max_infant}
          maxChildren={listing?.max_child}
          filters={filters}
          updateGuestCount={updateGuestCount}
          showRooms={showRooms}
        />
      </div>

      <div className="mt-6 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={confirm}
          className="w-full bg-gray-900 text-white py-3 rounded-lg font-medium shadow-sm"
        >
          {doneLabel}
        </button>
      </div>
    </motion.div>
  );
};

export default BookingGuestModal;
